import NiceModal from "@ebay/nice-modal-react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "@bounce/shared/contexts";
import {formatCurrency} from "@bounce/shared/helpers";
import {AccountExternal} from "@bounce/shared/consumer/declarations";
import {IconWarningGreen} from "@bounce/shared/consumer/bounce-ui";
import ContactUsDialog from "~/components/contact-us/ContactUsDialog";
import AccountDetailsDialog from "~/pages/account/account/components/AccountDetailsDialog";
import {useGetUserQuery} from "~/api/requests";
import {usePageView} from "~/hooks";
import {useAmplitude} from "~/contexts";
import {Pages} from "~/bi-events/enum";
import {urls} from "~/routes/consumerUrls";
import {Button, Card, Container, Entrance, Text} from "~/new-design/components";
import {PageContent} from "~/new-design/layout";
import {spacing} from "~/new-design/theme";
import {AccountCard} from "~/new-design/pages/welcome/components/AccountCard";
import {buildAccountDetails} from "~/new-design/pages/welcome/components/WelcomeHeroSection";
import {getBlockedStatusChip} from "./utils";

type BlockedAccountPageProps = {
  account: AccountExternal;
  accountId: string;
  text: string;
  reason: string;
};

export const BlockedAccountPage = ({account, accountId, text, reason}: BlockedAccountPageProps) => {
  const navigate = useNavigate();
  const {loggedIn, userId} = useAuth();
  const {pageClickEvent} = useAmplitude();
  const {data: user} = useGetUserQuery({});

  const isMultiAccount = (user?.accounts_switcher?.length ?? 0) > 1;
  const showGoToAccounts = loggedIn && isMultiAccount;
  usePageView(Pages.ACCOUNT, {type: "blocked_account", reason, isMultiAccount}, !!user || !userId);

  const handleViewDetails = () => {
    pageClickEvent({page: Pages.ACCOUNT, cta: "view_details", data: {reason}});
    NiceModal.show(AccountDetailsDialog, {accountId});
  };

  const handleContactUs = () => {
    pageClickEvent({page: Pages.ACCOUNT, cta: "contact_us", data: {reason}});
    NiceModal.show(ContactUsDialog, {fetchContact: loggedIn});
  };

  const handleGoToAccounts = () => {
    pageClickEvent({page: Pages.ACCOUNT, cta: "go_to_accounts", data: {reason}});
    navigate(urls.accounts.url);
  };

  const title = reason === "active_dispute" ? "Your debt is in dispute" : "Important notice";

  return (
    <PageContent>
      <Entrance variant="slide">
        <Container gap={4}>
          <AccountCard
            creditorName={account.partner_marketing_name || ""}
            partnerLogoUrl={account.partner_symbol_url}
            balance={formatCurrency(account.total_balance ?? 0)}
            status={getBlockedStatusChip(reason)}
            details={buildAccountDetails(account)}
            inlineToggle
            page={Pages.ACCOUNT}
            onViewDetails={handleViewDetails}
          />
          <Card sx={{gap: spacing[3], padding: spacing[5]}}>
            <Container row gap={2} sx={{alignItems: "center"}}>
              <IconWarningGreen />
              <Text variant="label-lg">{title}</Text>
            </Container>
            <Text variant="body">{text}</Text>
            <Container gap={1}>
              <Button variant="filled" fullWidth onClick={handleContactUs}>
                Contact us
              </Button>
              {showGoToAccounts && (
                <Button variant="outline" fullWidth onClick={handleGoToAccounts}>
                  Go to my accounts
                </Button>
              )}
            </Container>
          </Card>
        </Container>
      </Entrance>
    </PageContent>
  );
};
